import React from 'react';
import { Leaf, ArrowLeft, Target, Users, Lightbulb, Heart, Shield } from 'lucide-react';

const COMPANY_VALUES = [
  {
    icon: Heart,
    title: "Consumer First",
    description: "Everything we do is focused on helping cannabis consumers save money and make informed decisions."
  },
  {
    icon: Shield,
    title: "Transparency",
    description: "We believe in honest pricing, clear terms, and transparent business practices. No hidden fees or surprises."
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "We continuously improve our platform with new features and technologies to better serve our users."
  },
  {
    icon: Users,
    title: "Community",
    description: "We're building a community of smart cannabis consumers who share knowledge and save money together."
  }
];

const ValueCard = ({ value, index }) => {
  const Icon = value.icon;
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="w-12 h-12 bg-gradient-to-r from-green-100 to-emerald-200 rounded-lg flex items-center justify-center mb-4">
        <Icon className="text-green-600" size={24} />
      </div>
      <h3 className="font-bold text-gray-900 text-lg mb-3">{value.title}</h3>
      <p className="text-gray-600">{value.description}</p>
    </div>
  );
};

const About = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back</span>
              </button>
              <div className="flex items-center space-x-2">
                <Leaf className="text-green-600" size={32} />
                <span className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  WeederBoard
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-6">
              About WeederBoard
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              We're on a mission to help cannabis consumers make smarter purchasing decisions and save money.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Our Story</h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-6 text-center">
                  Weederboard was born to solve the problems that plague cannabis consumers every day. 
                </p>
                <p className="mb-6 text-center">
                Who has my favorite strain in stock? Where is bud from my favorite farm available? Who's got the best deal on rosin around? </p>
                <p className="mb-6 text-center">
                  When a group of friends working at a cannabis cultivation facility realized their favorite deal on rosin had ended, a choice had to be made. Give up, and just accept the next best deal, or do something about it.               </p>
                <p className="mb-6 text-center">
                  We decided to solve this problem by creating a centralized platform that aggregates deals from all dispensaries across New England. What started as a simple price comparison tool has evolved into a comprehensive platform that helps consumers save real money on their cannabis purchases, and find their preferred products across the region.
                </p>
                <p className="mb-6 text-center">
                  Today, WeederBoard tracks deals from 500+ dispensaries across ALL of Massachusetts, and some from parts of Maine, New York, Vermont, and Rhode Island, and we're expanding everyday!
                </p>
                <p className="mb-6 text-center">
                  We're just getting started. Our vision is to become the go-to platform for cannabis consumers who want to make informed, money-saving decisions about their purchases.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Our Values */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Our Values
              </h2>
              <p className="text-xl text-gray-600">
                The principles that guide everything we do
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {COMPANY_VALUES.map((value, index) => (
                <ValueCard key={index} value={value} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <Target className="text-green-600 mx-auto mb-6" size={48} />
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-xl text-gray-600 mb-8">
                To democratize access to cannabis pricing and product information and empower consumers to make informed purchasing decisions that save them money and get them the products they want.
              </p>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Looking Ahead</h3>
                <p className="text-gray-600">
                  We're continuously working on new features including community reviews, daily deal notifications, and expanding coverage to serve even more cannabis consumers across the country.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Want to Learn More?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              We love connecting with our community and hearing your feedback.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                Contact Us
              </button>
              <button className="border-2 border-gray-300 text-gray-700 px-8 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300">
                Join Our Community
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Leaf className="text-green-400" size={24} />
            <span className="text-xl font-bold">WeederBoard</span>
          </div>
          <p className="text-gray-400">
            The smartest way to find deals on cannabis products and save money.
          </p>
          <div className="border-t border-gray-800 mt-8 pt-8">
            <p className="text-gray-400">&copy; 2025 WeederBoard. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
